import type { OptionResult, QuestionResult, ScreenResult } from '@floorpoll/shared';

/**
 * Story #79 — what every results export is built from.
 *
 * Lifted out of the CSV (#47) when the workbook arrived rather than invented
 * for it. The two formats are the same facts at the same grain, and a reader
 * who opens both should not find them disagreeing about what a row is; sharing
 * the headers and the row walk is what makes that structural instead of a
 * thing someone has to keep checking by eye.
 *
 * What deliberately does not live here is the escaping. `FORMULA_STARTERS`,
 * the leading apostrophe and the `BOM` are answers to a CSV being a text file
 * a spreadsheet guesses at, and they stay in `resultsCsv.ts` — a workbook
 * holds typed cells, so applying them there would corrupt an author's option
 * text rather than defend it.
 */
export interface ResultsSource {
  /** The poll's title — the run's frozen one when exporting a run. */
  title: string;
  /** The run this describes, or null for the accumulated total. */
  runDate: string | null;
  screens: ScreenResult[];
}

/** What an export puts in a column that is about one evening. */
export const ALL_RUNS = 'All runs';

/**
 * The facts both formats carry, in both formats' column order.
 *
 * A format may add a column of its own after these — the workbook does — but
 * none may drop one or reorder them.
 */
export const RESULT_HEADERS = [
  'Run date',
  'Screen',
  'Question',
  'Option',
  'Count',
  'Percentage',
] as const;

/** One option's row, with the question and screen it belongs to in reach. */
export interface ResultRow {
  /** The run's date, or `ALL_RUNS` for the accumulated export. */
  runDate: string;
  /** 1-based, because it is a label for a reader rather than an index. */
  screenNumber: number;
  question: QuestionResult;
  option: OptionResult;
}

/**
 * One row per option, which is the grain the numbers live at: a reader can
 * pivot rows up to a question or a screen, and could not split them back down.
 */
export function resultRows(source: ResultsSource): ResultRow[] {
  const runDate = source.runDate ?? ALL_RUNS;

  return source.screens.flatMap((screen, index) =>
    screen.questions.flatMap((question) =>
      question.options.map((option) => ({
        runDate,
        screenNumber: index + 1,
        question,
        option,
      })),
    ),
  );
}

/**
 * "Kickoff poll" → "kickoff-poll-results.xlsx", plus the date for one run.
 *
 * The extension is a parameter rather than baked in, so the two formats cannot
 * drift into naming the same export differently.
 */
export function resultsFilename(source: ResultsSource, extension: string): string {
  const slug =
    source.title
      .toLowerCase()
      // Diacritics are folded onto their base letter before the strip below,
      // so "Zürich" becomes "zurich" rather than "z-rich". The filename stays
      // ASCII on purpose — it crosses filesystems the app knows nothing about
      // — but a title in a European language should still be readable in it.
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'poll';

  // Just the day: a filename is for finding the file again, and the run page
  // it came from carries the time.
  const day = source.runDate?.slice(0, 10);
  return day ? `${slug}-${day}.${extension}` : `${slug}-results.${extension}`;
}

/**
 * Hands the file to the browser.
 *
 * An object URL rather than a `data:` one: Safari caps a navigable data URL at
 * a size a large poll can reach, and the revoke below is what keeps the blob
 * from living as long as the tab.
 *
 * The blob arrives already carrying its own MIME type, so there is no second
 * place for a format to declare what it is.
 */
export function download(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
